/**
 * Mines the ranger can sense (04 §4: a perk or trait sets `world.mods.mineSenseRadius`).
 * Every LIVE mine within that radius of him shows as a small dark disc on the ground;
 * beyond it, or with no such perk, nothing is drawn. `Actors.update` drives this - the app
 * needs to do nothing.
 */

import { type BufferGeometry, type Material, Mesh, type Scene, type Texture } from 'three';
import { senseRadius } from '../../sim/mods';
import { Pcg32 } from '../../sim/rng';
import type { World } from '../../sim/types';
import { mineDisc } from '../kit/groundTells';
import { createToonMaterial, OUTLINE } from '../materials/toon';
import type { Palette } from '../palette';

export class MineMarkers {
  private readonly geometry: BufferGeometry;
  private readonly material: Material;
  /** Grows to the most mines ever in range at once; spare discs are hidden, not destroyed. */
  private readonly pool: Mesh[] = [];

  constructor(
    private readonly scene: Scene,
    palette: Palette,
    gradient: Texture,
    private readonly groundY: (x: number, z: number) => number,
  ) {
    this.geometry = mineDisc(palette, new Pcg32(1, 1)).build();
    this.material = createToonMaterial({ gradientMap: gradient, outline: OUTLINE.prop });
  }

  update(world: World): void {
    const r = world.ranger;
    // R5: less of the ground is read at a run, which is what the perk always said (`senseRadius`).
    const radius = world.insertion.phase === 'ground' ? senseRadius(world.mods, r.running) : 0;
    let used = 0;
    if (radius > 0) {
      for (const mine of world.hazards.mines) {
        if (!mine.live) continue;
        const dx = mine.x - r.x;
        const dz = mine.z - r.z;
        if (dx * dx + dz * dz > radius * radius) continue;
        let disc = this.pool[used];
        if (!disc) {
          disc = new Mesh(this.geometry, this.material);
          disc.receiveShadow = true;
          this.pool.push(disc);
          this.scene.add(disc);
        }
        // Mines do not move, so no texel snap is needed: static geometry is already pixel-stable.
        disc.position.set(mine.x, this.groundY(mine.x, mine.z) + 0.01, mine.z);
        disc.visible = true;
        used++;
      }
    }
    for (let i = used; i < this.pool.length; i++) {
      const spare = this.pool[i];
      if (spare) spare.visible = false;
    }
  }

  dispose(): void {
    for (const disc of this.pool) this.scene.remove(disc);
    this.pool.length = 0;
    this.geometry.dispose();
    this.material.dispose();
  }
}
